import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Send } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { callFunction } from "@/lib/supabase";
import { toast } from "sonner";

const schema = z.object({
  title: z.string().min(2),
  body: z.string().min(2),
});
type FormValues = z.infer<typeof schema>;

export function AdminBroadcast() {
  const { t } = useTranslation();
  const form = useForm<FormValues>({ resolver: zodResolver(schema) });

  const send = useMutation({
    mutationFn: (data: FormValues) => callFunction<{ count: number }>("admin-broadcast", data),
    onSuccess: (r) => {
      toast.success(t("admin.broadcastForm.sent", { count: r.count }));
      form.reset();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Erreur"),
  });

  return (
    <Card className="max-w-2xl">
      <CardContent className="py-4 space-y-3">
        <form onSubmit={form.handleSubmit((d) => send.mutate(d))} className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="title">{t("admin.broadcastForm.title")}</Label>
            <Input id="title" {...form.register("title")} />
            {form.formState.errors.title && <p className="text-sm text-destructive">{form.formState.errors.title.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="body">{t("admin.broadcastForm.body")}</Label>
            <textarea
              id="body"
              {...form.register("body")}
              className="w-full min-h-24 rounded-lg border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            {form.formState.errors.body && <p className="text-sm text-destructive">{form.formState.errors.body.message}</p>}
          </div>

          <Button type="submit" size="lg" className="w-full" loading={send.isPending}>
            <Send /> {t("admin.broadcastForm.send")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
